import * as React from 'react';
import { Typography } from 'antd';
import AbjustColor from './AbjustColor';
import IframeServiceInjector from '../../services/injectors/IframeServiceInjector';
import FrameEventMessanger from '../../services/frames/FrameEventMessanger';
import { CHANGE_SITE_COLOR } from '../../consts/events';
import { observer } from 'mobx-react-lite';
import SiteBuilderModel from '../../model/SiteBuilderModel';
import { toJS } from 'mobx';
import chroma from 'chroma-js';
import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BodyText } from '../../../../components/ui/Typography';
import DesignModel from '../../../Design/models/DesignModel';

const { Text } = Typography;

interface FCProps {
  onResetRef?: React.MutableRefObject<(() => void) | null>;
}

const COLOR_LABELS: Record<string, string> = {
  background: 'Background',   
  primary: 'Highlight',       
  secondary: 'Contrast',      
  surface: 'Surface',         
};

const COLOR_ORDER = ['background', 'primary', 'secondary', 'surface'];

const AbjustColors: React.FC<FCProps> = observer(({ onResetRef }) => {
  const { t } = useTranslation();
  let finalColorSchema = SiteBuilderModel.finalColorScheme;
  const [resetKey, setResetKey] = React.useState(0);

  const hiddenColorKeys = ['surface-elevated', 'tertiary', 'warning', 'border'];

  let colorPickersJSX = [];

  const orderedKeys = [
    ...COLOR_ORDER.filter((key) => key in finalColorSchema && !hiddenColorKeys.includes(key)),
    ...Object.keys(finalColorSchema).filter(
      (key) => !COLOR_ORDER.includes(key) && !hiddenColorKeys.includes(key)
    ),
  ];

  orderedKeys.forEach((key) => {
    const colorData = finalColorSchema[key];
    let contrast = null;

    if (colorData.main && colorData.text) {
      contrast = chroma.contrast(colorData.main, colorData.text);
    }

    colorPickersJSX.push(
      <div
        key={`${key}-${resetKey}`}
        className="flex justify-between text-center items-start border-solid border-[1px] w-full p-[24px] border-solid border-[rgba(0,0,0,0.15)] border rounded-xl">
        <div className="flex flex-col gap-[8px]">
          <div>
            {colorData.main && (
              <>
                <div className="border-solid border-[1px] border-[rgba(0,0,0,0.15)] p-[4px]">
                  <AbjustColor
                    key={`main-${key}-${resetKey}`}
                    defaultValue={colorData.main}
                    textColor={colorData.text}
                    exampleText={
                      <span style={{ fontWeight: 400, fontSize: 14 }}>
                        {t('Example')}
                      </span>
                    }
                    onChange={(color) => {
                      IframeServiceInjector.getService<FrameEventMessanger>(
                        'FrameEventMessanger'
                      ).sendMessage(CHANGE_SITE_COLOR, {
                        color: color,
                        type: key,
                        category: 'main'
                      });
                    }}
                    onOpenChange={(color, open) => {
                      if (open === false) {
                        let currentColorSchema = toJS(
                          SiteBuilderModel.finalColorScheme
                        );

                        currentColorSchema[key].main = color;

                        SiteBuilderModel.setFinalColorScheme(
                          currentColorSchema
                        );
                      }
                    }}></AbjustColor>
                </div>
              </>
            )}
          </div>

          <div>
            <BodyText className='font-bold' style={{marginBottom: '0', fontSize: '14px'}}>{t(COLOR_LABELS[key] || key)}</BodyText>
          </div>
        </div>

        <div className="flex flex-col gap-[8px]">
          {colorData.text && (
            <>
              <div className="border-solid border-[1px] border-[rgba(0,0,0,0.15)] p-[4px]">
                <AbjustColor
                  key={`text-${key}-${resetKey}`}
                  defaultValue={colorData.text}
                  onChange={(color) => {
                    IframeServiceInjector.getService<FrameEventMessanger>(
                      'FrameEventMessanger'
                    ).sendMessage(CHANGE_SITE_COLOR, {
                      color: color,
                      type: key,
                      category: 'text'
                    });
                  }}
                  onOpenChange={(color, open) => {
                    if (open === false) {
                      let currentColorSchema = toJS(
                        SiteBuilderModel.finalColorScheme
                      );

                      currentColorSchema[key].text = color;

                      SiteBuilderModel.setFinalColorScheme(currentColorSchema);
                    }
                  }}
                />
              </div>

              <BodyText style={{ marginBottom: '0', fontSize: '14px' }}>Text</BodyText>

            </>
          )}
        </div>

        <div className="flex justify-between gap-[12px] flex-col justify-end">
          {contrast && (
            <div className="flex flex-col gap-[20px] items-start">
              <Text className="font-bold">{t('Contrast')}:</Text>
              <Text>{contrast.toFixed(1)}:1</Text>
            </div>
          )}

          <div className="flex items-center">
            {contrast && contrast > 4.5 && (
              <CheckOutlined style={{ color: '#52C41A' }} />
            )}

            {contrast && contrast < 4.5 && (
              <WarningOutlined style={{ color: '#DB3635' }} />
            )}
            <BodyText className="font-bold ml-2" style={{ marginBottom: '0' }}>
              BFSG
            </BodyText>
          </div>
        </div>
      </div>
    );
  });

  const resetColors = () => {
    const originalColorScheme = DesignModel.getFinalColorScheme;
    
    SiteBuilderModel.setFinalColorScheme(originalColorScheme);
    
    Object.keys(originalColorScheme).forEach((key) => {
      const colorData = originalColorScheme[key];
      
      if (colorData.main) {
        IframeServiceInjector.getService<FrameEventMessanger>(
          'FrameEventMessanger'
        ).sendMessage(CHANGE_SITE_COLOR, {
          color: colorData.main,
          type: key,
          category: 'main'
        });
      }
      
      if (colorData.text) {
        IframeServiceInjector.getService<FrameEventMessanger>(
          'FrameEventMessanger'
        ).sendMessage(CHANGE_SITE_COLOR, {
          color: colorData.text,
          type: key,
          category: 'text'
        });
      }
    });
    
    setResetKey(prev => prev + 1);
  };

  React.useEffect(() => {
    if (onResetRef) {
      onResetRef.current = resetColors;
    }
  }, [onResetRef]);

  return <div className="flex flex-col gap-[24px]">{colorPickersJSX}</div>;
});

export default AbjustColors;
